import React, {useState} from 'react';
import {useForm} from 'react-hook-form';
import userService from '../../../../../services/user.service';
import clinicService from '../../../../../services/clinic.service';
import {toast} from 'react-toastify';

const ClinicUserReminderSettingsForm = ({reminderSettings, user, updateSettings}) => {
    const {register, handleSubmit, reset, formState: {errors}} = useForm({
        defaultValues: {
            ...reminderSettings,
            apptSummary: reminderSettings.apptSummary || "ECP",
            callPreference: reminderSettings.callPreference || "HOME_PHONE"
        }
    });

    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [resetSuccess, setResetSuccess] = useState(false);

    const onSubmit = async (data) => {
        setIsLoading(true);
        setIsError(false);

        try {
            const formattedData = {
                ...data,
                id: reminderSettings.id,
                clinicId: user.clinicId,
                userId: user.id,
                daysPrebookReminder: parseInt(data.daysPrebookReminder),
                daysFirstReminder: parseInt(data.daysFirstReminder),
                daysSecondReminder: parseInt(data.daysSecondReminder),
                minutesToAppt: parseInt(data.minutesToAppt)
            };

            const response = await userService.updateUserReminderSettings(
                user.clinicId,
                user.id,
                formattedData
            );

            if (updateSettings && typeof updateSettings === 'function') {
                updateSettings(response.data);
            }
        } catch (err) {
            setIsError(true);
            setErrorMessage(err.message || "Error Updating User Reminder Settings");
        } finally {
            setIsLoading(false);
        }
    };

    const getClinicReminders = async () => {
        try {
            setIsLoading(true);
            const response = await clinicService.getReminderSettings(user.clinicId);
            const clinicSettings = {
                ...response.data,
                id: reminderSettings.id,
                clinicId: user.clinicId,
                userId: user.id
            };
            reset(clinicSettings);

            // Show success state
            setResetSuccess(true);
            setTimeout(() => setResetSuccess(false), 2000);
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const renderSwitch = (name, label) => (
        <div className="list-group-item d-flex justify-content-between align-items-center py-2">
            <div className="d-flex align-items-center">
                <span>{label}</span>
            </div>
            <div className="form-check form-switch">
                <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    {...register(name)}
                />
            </div>
        </div>
    );

    return (
        <>
            <div className="d-flex justify-content-end mb-3">
                <button
                    className="btn btn-warning btn-sm"
                    onClick={getClinicReminders}
                    disabled={isLoading}
                >
                    {resetSuccess ? (
                        <>
                            <i className="bi bi-check me-1"></i> Reset Complete
                        </>
                    ) : (
                        <>
                            <i className="bi bi-arrow-counterclockwise me-1"></i> Reset to Clinic Settings
                        </>
                    )}
                </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="row">
                    <div className="col-md-7">
                        <div className="card card-padding card-border shadow-none mb-4">
                            <div className="card-title">Reminder Settings</div>
                            <div className="card-body">
                                <div className="list-group list-group-flush">
                                    {renderSwitch("apptReminder", "Appointment Reminders")}
                                    {renderSwitch("isPrebookedReminder", "Prebooked Reminders")}
                                    {renderSwitch("isFirstReminder", "First Reminders")}
                                    {renderSwitch("isSecondReminder", "Second Reminders")}
                                    {renderSwitch("isLastReminder", "Last Reminders")}
                                    {renderSwitch("isSameDayReminder", "Same Day Reminders")}
                                    {renderSwitch("isClReplacement", "Contact Lens Reminders")}
                                    {renderSwitch("isClReorder", "Contact Lens Reorder")}
                                    {renderSwitch("isBirthday", "Birthday Reminders")}
                                    {renderSwitch("isThankyou", "Thank You Reminder")}
                                    {renderSwitch("isWelcome", "Welcome Reminders")}
                                    {renderSwitch("isEmail", "Email Reminder")}
                                    {renderSwitch("isSms", "SMS Reminders")}
                                    {renderSwitch("isCall", "Call Reminders")}
                                    {renderSwitch("isApptSummary", "Appointment Summary")}
                                    {renderSwitch("isGlPickup", "Glasses Pickup")}
                                    {renderSwitch("isClPickup", "Contact Lens Pickup")}
                                    {renderSwitch("isFollowup", "Follow Up")}
                                    {renderSwitch("isClGlSummary", "Contact Lens & Glass Summary")}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-md-5">
                        <div className="card card-padding card-border shadow-none mb-4">
                            <div className="card-title">Reminder Preferences</div>
                            <div className="card-body">
                                <div className="mb-3">
                                    <label className="form-label">Call Preference</label>
                                    <select className="form-select" {...register("callPreference")}>
                                        <option value="DISABLED">Disabled</option>
                                        <option value="HOME_PHONE">Home Phone</option>
                                        <option value="CELL_PHONE">Cell Phone</option>
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Appointment Summary</label>
                                    <select className="form-select" {...register("apptSummary")}>
                                        <option value="CLINIC">Clinic</option>
                                        <option value="ECP">ECP</option>
                                        <option value="BOTH">Both</option>
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Reminder Time</label>
                                    <input
                                        type="time"
                                        className="form-control"
                                        step="1"
                                        {...register("reminderTime")}
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Same Day Reminder Time</label>
                                    <input
                                        type="time"
                                        className="form-control"
                                        step="1"
                                        {...register("sameDayReminderTime")}
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Days to Send Prebook Reminder</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        {...register("daysPrebookReminder", {valueAsNumber: true})}
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Days to Send First Reminder</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        {...register("daysFirstReminder", {valueAsNumber: true})}
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Days to Send Second Reminder</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        {...register("daysSecondReminder", {valueAsNumber: true})}
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Minutes Earlier</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        {...register("minutesToAppt", {valueAsNumber: true})}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="d-flex justify-content-between align-items-center mt-4">
                    <div>
                        {isError && (
                            <div className="alert alert-danger mb-0">
                                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                {errorMessage}
                            </div>
                        )}
                    </div>
                    <button
                        type="submit"
                        className="btn btn-success btn-sm"
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2"></span>
                                Saving...
                            </>
                        ) : (
                            <>
                                <i className="bi bi-floppy"></i> Save
                            </>
                        )}
                    </button>
                </div>
            </form>
        </>
    );
};

export default ClinicUserReminderSettingsForm;